import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Save, CheckCircle2, ArrowLeft, Calendar as CalIcon } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TimeSelect, parseTime, composeTime } from "@/components/TimeSelect";

const input = "w-full rounded-sm bg-input border border-gold/20 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold transition-shadow duration-200";
const label = "block text-sm font-medium text-slate-300 mb-1.5";

export default function ManageBooking() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [saved, setSaved] = useState(false);
  const [booking, setBooking] = useState(null);
  const [date, setDate] = useState(undefined);
  const [form, setForm] = useState({ name: "", phone: "", email: "", pickup: "", dropoff: "", notes: "", hour: "", minute: "", ampm: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/bookings/${id}`);
        setBooking(data);
        const t = parseTime(data.time);
        setForm({
          name: data.name && data.name !== "Customer" ? data.name : "", phone: data.phone || "", email: data.email || "",
          pickup: data.pickup || "", dropoff: data.dropoff || "", notes: data.notes || "",
          hour: t.hour, minute: t.minute, ampm: t.ampm,
        });
        if (data.date) { const d = new Date(data.date + "T00:00:00"); if (!isNaN(d)) setDate(d); }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim() || null, phone: form.phone.trim() || null, email: form.email.trim() || null,
        pickup: form.pickup.trim() || null, dropoff: form.dropoff.trim() || null, notes: form.notes.trim() || null,
        date: date ? format(date, "yyyy-MM-dd") : null,
        time: composeTime(form.hour, form.minute, form.ampm) || null,
      };
      const { data } = await api.put(`/bookings/${id}/customer-edit`, payload);
      setBooking(data.booking);
      setSaved(true);
      toast.success("Your booking has been updated.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update your booking.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="grid place-items-center py-40"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  if (notFound) return (
    <section className="max-w-xl mx-auto px-5 py-32 text-center">
      <h1 className="font-heading text-3xl font-bold text-white">Booking not found</h1>
      <p className="text-slate-400 mt-3">We couldn't find this booking. Please check your link or contact us.</p>
      <Link to="/" className="mt-6 inline-flex items-center gap-2 text-gold hover:text-gold-light"><ArrowLeft className="h-4 w-4" /> Back to home</Link>
    </section>
  );

  return (
    <section className="max-w-2xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid="manage-booking-page">
        <span className="text-sm uppercase tracking-[0.2em] text-gold">Manage booking</span>
        <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">Edit your ride</h1>
        <p className="mt-3 text-slate-300">Reference <span className="text-gold font-semibold">{booking.id.slice(0, 8).toUpperCase()}</span> · {booking.description}</p>
        <p className="mt-1 text-sm text-slate-400">Total fare: <span className="text-gold font-semibold">${(booking.amount || 0).toFixed(2)} {booking.currency || "CAD"}</span> · update any details below and save. All fields are optional.</p>

        {saved && (
          <div className="mt-6 flex items-center gap-2 rounded-sm bg-emerald-500/10 border border-emerald-500/30 px-4 py-3 text-emerald-300" data-testid="manage-saved">
            <CheckCircle2 className="h-5 w-5" /> Your changes have been saved.
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-5 rounded-sm bg-navy-light border border-gold/20 p-6 sm:p-8" data-testid="manage-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className={label}>Full name</label><input data-testid="manage-name" value={form.name} onChange={set("name")} placeholder="Your name" className={input} /></div>
            <div><label className={label}>Phone</label><input data-testid="manage-phone" value={form.phone} onChange={set("phone")} placeholder="Phone number" className={input} /></div>
          </div>
          <div><label className={label}>Email</label><input data-testid="manage-email" type="email" value={form.email} onChange={set("email")} placeholder="Email address" className={input} /></div>
          <div><label className={label}>Pickup location</label><input data-testid="manage-pickup" value={form.pickup} onChange={set("pickup")} placeholder="Address or airport terminal" className={input} /></div>
          <div><label className={label}>Drop-off location</label><input data-testid="manage-dropoff" value={form.dropoff} onChange={set("dropoff")} placeholder="Destination address" className={input} /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={label}>Pickup date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="manage-date-trigger" className={`${input} flex items-center gap-2 text-left ${!date && "text-slate-500"}`}>
                    <CalIcon className="h-4 w-4 text-gold" />{date ? format(date, "PPP") : "Select date"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-navy-light border-gold/30" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className={label}>Pickup time</label>
              <TimeSelect idPrefix="manage-time" hour={form.hour} minute={form.minute} ampm={form.ampm}
                onHour={(v) => setForm({ ...form, hour: v })} onMinute={(v) => setForm({ ...form, minute: v })} onAmpm={(v) => setForm({ ...form, ampm: v })} />
            </div>
          </div>
          <div><label className={label}>Notes</label><textarea data-testid="manage-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder="Flight number, special requests…" className={`${input} resize-none`} /></div>
          <button type="submit" disabled={saving} data-testid="manage-save-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold py-3.5 font-semibold text-navy hover:bg-gold-light transition-colors duration-200 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
          </button>
          <p className="text-xs text-slate-500 text-center">Need to change the fare or cancel? Please contact us directly.</p>
        </form>
      </motion.div>
    </section>
  );
}
